// Product option modal — Selection rules: quantity modes, min/max limits and the validation
// messages shown when a group is incomplete.
import { getComboGroupSelectedCount } from '@core/utils/comboSelection';

// Variant-SKU options that can't be picked: faded (no valid combination with
// the current selections) or out of stock for that combination. The already
// selected option stays clickable so it can be deselected.
export function isSkuOptionBlocked(state) {
  return Boolean(state) && (!state.isCompatible || state.isOutOfStock);
}

// Variant-SKU option state → modifier classes.
export function skuStateClass(state) {
  if (!state) return '';
  if (!state.isCompatible) return ' is-sku-faded';
  if (state.isOutOfStock) return ' is-sku-out';
  return '';
}

export function hasQuantityMode(group, option) {
  return Boolean(
    group?.allowQuantity ||
    group?.minModifier > 0 ||
    group?.maxModifier > 0 ||
    (group?.isSingleList && (option?.minQuantity > 0 || option?.maxQuantity > 0)),
  );
}

export function sumQuantities(record) {
  return Object.values(record || {}).reduce((sum, value) => sum + (Number(value) || 0), 0);
}

export function distinctCount(record) {
  return Object.values(record || {}).filter((value) => Number(value) > 0).length;
}

// For normal modifier groups, max_modifier is the total quantity cap.
// max_modifier: 0 means unlimited, even if individual modifiers carry max_quantity.
export function getModifierGroupMaxQuantity(group, option, selection) {
  if (group?.isSingleList) {
    return option?.maxQuantity > 0 ? option.maxQuantity : 0;
  }

  if (group?.maxModifier > 0) {
    const currentQty = Number(selection?.[option.id] ?? 0);
    const otherQty = Math.max(0, sumQuantities(selection) - currentQty);
    return currentQty + Math.max(0, group.maxModifier - otherQty);
  }

  return 0;
}

export function buildModifierGroupError(group, selection) {
  const selectedCount = distinctCount(selection);
  const totalQty = sumQuantities(selection);
  const requirements = [];

  if (group.minSelection > 0 && selectedCount < group.minSelection) {
    requirements.push(`${group.minSelection} selections`);
  }
  if (group.maxSelection > 0 && selectedCount > group.maxSelection) {
    requirements.push(`at most ${group.maxSelection} selections`);
  }
  if (group.minModifier > 0 && totalQty < group.minModifier) {
    requirements.push(`${group.minModifier} total quantity`);
  }
  if (group.maxModifier > 0 && totalQty > group.maxModifier) {
    requirements.push(`at most ${group.maxModifier} total quantity`);
  }

  if (requirements.length === 0) {
    return null;
  }

  return `${group.name} requires ${requirements.join(' and ')}`;
}

export function buildComboGroupError(group, selectedCount) {
  if (group.minSelect > 0 && selectedCount < group.minSelect) {
    return group.isMultiSelect
      ? `Select at least ${group.minSelect} for ${group.name}`
      : `Select one option for ${group.name}`;
  }
  return null;
}

/**
 * Every reason the current selection can't be added yet (first one is shown
 * on the CTA). Variant-SKU items only need a resolved SKU; other items need
 * each required variant group, and every modifier / combo group within limits.
 */
export function collectValidationErrors({
  isVariantSku,
  variantGroups,
  variantSelections,
  selectedSku,
  modifierGroups,
  modifierSelections,
  comboGroups,
  comboPositionState,
  itemUpsellGroups = [],
  itemUpsellSelections = {},
}) {
  const errors = [];
  for (const group of isVariantSku ? [] : variantGroups) {
    if (group.required && !variantSelections[group.id]) {
      errors.push('Select one variation');
    }
  }
  if (isVariantSku && !selectedSku) {
    errors.push('Select one variation');
  }

  for (const group of modifierGroups) {
    const selection = modifierSelections[group.id] || {};
    const totalQty = sumQuantities(selection);
    const shouldValidateQuantityRange = Boolean(group.required || group.minSelection > 0);

    if (shouldValidateQuantityRange || totalQty > 0) {
      const groupError = buildModifierGroupError(group, selection);
      if (groupError) errors.push(groupError);
    }

    if (group.isSingleList) {
      for (const option of group.options) {
        const optionQty = selection[option.id] ?? 0;
        if (optionQty > 0 && option.minQuantity > 0 && optionQty < option.minQuantity) {
          errors.push(`${option.name} needs at least ${option.minQuantity}`);
        }
        if (option.maxQuantity > 0 && optionQty > option.maxQuantity) {
          errors.push(`${option.name} allows at most ${option.maxQuantity}`);
        }
      }
    }
  }

  for (const group of comboGroups) {
    const selectedCount = getComboGroupSelectedCount(group, comboPositionState);
    const groupError = buildComboGroupError(group, selectedCount);
    if (groupError) errors.push(groupError);
  }

  for (const group of itemUpsellGroups) {
    if (group.required && distinctCount(itemUpsellSelections[group.id]) === 0) {
      errors.push(`Select one option for ${group.name}`);
    }
  }
  return errors;
}
